import { processesApi, processesUploadsApi, uploadsApi } from "api";
import uploadBytesApi from "api/uploadBytesApi";
import { IMAGE_EXTENSIONS, getFileExtension } from "common/utilities";
import { Attachment } from "components/AttachmentList";
import { BaseTask } from "model/activitiesModel";
import { useCallback, useEffect, useState } from "react";

const useAttachments = (
  task: BaseTask | null
): [
    Attachment[],
    React.Dispatch<React.SetStateAction<Attachment[]>>,
    () => Promise<void>,
    { [key: string]: string },
    { [key: string]: any },
    boolean
  ] => {
  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const [images, setImages] = useState<{ [key: string]: string }>({});
  const [histories, setHistories] = useState<{ [key: string]: any }>({});
  const [loadingAttachments, setLoadingAttachments] = useState(false);

  const download = async (attachment: Attachment) => {
    // Byte dal backend statistiche (stessa sorgente della pagina pubblica, che
    // funziona anche da sloggati): il content-type arriva dalla risposta, così
    // la miniatura è un blob URL col tipo giusto. Fallback sull'endpoint Delphi.
    try {
      const { data, headers } = await uploadBytesApi.get(`/${attachment.id}`);
      return URL.createObjectURL(
        new Blob([data], { type: (headers?.["content-type"] as string) || "" })
      );
    } catch {
      try {
        const { data } = await processesUploadsApi.get(`/${attachment.id}`);
        return data.blob;
      } catch {
        return undefined;
      }
    }
  };

  const history = async (attachment: Attachment) => {
    try {
      const { data } = await uploadsApi.get(`/${attachment.id}/histories`);
      return data.uploads;
    } catch {
    } finally {
    }
  };

  const getAttachments = useCallback(async () => {
    if (task) {
      setLoadingAttachments(true);
      try {
        setLoadingAttachments(true);
        const response = await processesApi.get(
          `${task.idprocess}/attachments/light`
        );
        const promises = [];
        const ids = [];
        const promises_histories = [];
        const ids_histories = [];
        for (let attachment of response.data.attachments) {
          // Si scaricano i byte SOLO delle immagini: sono le uniche la cui tile
          // mostra una miniatura e le uniche che album ed export PDF leggono.
          // Per gli altri tipi la tile è l'icona, quindi scaricarli all'apertura
          // è peso per niente: si prendono al click (anteprima o download).
          if (
            IMAGE_EXTENSIONS.includes(
              (getFileExtension(attachment.filename) || "").toLowerCase()
            )
          ) {
            promises.push(download(attachment));
            ids.push(attachment.id);
          }
          promises_histories.push(history(attachment));
          ids_histories.push(attachment.id);
        }
        const result = await Promise.all(promises);
        const result_histories = await Promise.all(promises_histories);
        setAttachments(() => [...response.data.attachments]);
        setImages(
          Object.fromEntries(ids.map((key, index) => [key, result[index]]))
        );
        setHistories(
          Object.fromEntries(
            ids_histories.map((key, index) => [key, result_histories[index]])
          )
        );
      } catch {
      } finally {
        setLoadingAttachments(false);
      }
    }
  }, [task]);

  // Reset attachments when task changes to avoid showing stale data
  useEffect(() => {
    setAttachments([]);
    setImages({});
    setHistories({});
  }, [task?.idprocess]);

  return [attachments, setAttachments, getAttachments, images, histories, loadingAttachments];
};

export default useAttachments;
